import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export type Trigger = number;
/**
 * This hook sets up state and callback tuple that allows a parent component to invoke a callback within
 * a child component without using refs or inversion contexts. This is accomplished by simply incrementing
 * a number and passing that as a prop to a child component. Inside the child component, the companion hook
 * (useExecuteOnTrigger) is used to detect the number incrementing and then invoke a function as needed.
 */
export function useTrigger(): [Trigger, () => void] {
  const [trigger, setTrigger] = useState<Trigger>(0);
  const onTrigger = useCallback(() => setTrigger(trigger + 1), [trigger]);
  return [trigger, onTrigger];
}

/**
 * To be used inside a child component who's parent component is using useTrigger(). Note that
 * if the callback changes over time, it may be invoked if the trigger is non-zero. Please ensure
 * your callback is memoised or static.
 */
export function useExecuteOnTrigger(trigger: Trigger | undefined, callback: () => void) {
  useEffect(() => {
    if (!isNil(trigger) && trigger > 0) {
      callback();
    }
  }, [trigger, callback]);
}
